import type React from "react";

import type { SimpleProjectDto } from "@/api/types";
import { UploadFileButton } from "@/components/Button/UploadFileButton";
import type { Validation } from "@/components/DataTable/validation";
import { AlertTriangleIcon } from "@/components/Icons/AlertTriangleIcon";
import { Building04Icon } from "@/components/Icons/Building04Icon";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { Download01Icon } from "@/components/Icons/Download01Icon";
import { Tool01Icon } from "@/components/Icons/Tool01Icon";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { PillList } from "@/components/Pill/PillList";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { SectionCard } from "@/design-system/SectionCard";
import { useTranslation } from "@/translations";

interface BulkUploadAddressesValidationResultViewProps {
  dataState: Validation;
  permittedProjects: SimpleProjectDto[];
  maxAmountCorrections: number;
  onCancel: () => void;
  onUploadFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmitValidRows: () => void;
  onDownloadExampleFile: () => void;
}

export function BulkUploadAddressesValidationResultView({
  dataState,
  permittedProjects,
  maxAmountCorrections,
  onCancel,
  onUploadFile,
  onSubmitValidRows,
  onDownloadExampleFile,
}: BulkUploadAddressesValidationResultViewProps): React.ReactNode {
  const { t } = useTranslation();

  const amountValidRows = dataState.validRows.length;
  const amountInvalidRows = dataState.invalidRows.length;
  const projectColumnInvalidSummary = dataState.getInvalidSummaryByColumn("Project");

  return (
    <>
      {amountValidRows === 0 && (
        <PageGrid.Column size="100%">
          <SectionCard
            dataTestId="all-addresses-invalid-section"
            title={t("page.portfolio-address-bulk-upload.section.all-addresses-invalid.title")}
            description={t("page.portfolio-address-bulk-upload.section.all-addresses-invalid.description")}
            icon={<AlertTriangleIcon />}
          >
            <Button variant="ghostPrimary" icon={<Download01Icon />} onClick={onDownloadExampleFile}>
              {t("page.address-bulk-upload.example-file")}
            </Button>
            <footer className="mt-auto flex gap-space-sm">
              <Button variant="subtle" tone="error" onClick={onCancel}>
                {t("common.action.cancel")}
              </Button>
              <UploadFileButton
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                onChange={onUploadFile}
              >
                {t("page.portfolio-address-bulk-upload.btn.upload-corrected-excel")}
              </UploadFileButton>
            </footer>
          </SectionCard>
        </PageGrid.Column>
      )}
      {amountValidRows > 0 && (
        <PageGrid.Column size="100%">
          <SectionCard
            icon={<CheckIcon />}
            title={t("page.portfolio-address-bulk-upload.section.valid-addresses-to-import.title", {
              count: amountValidRows,
            })}
          >
            <p className="flex-1">
              {t("page.portfolio-address-bulk-upload.section.valid-addresses-to-import.description", {
                count: amountValidRows,
              })}
            </p>
            <Inline gap="sm" wrap={false}>
              <Button variant="subtle" tone="error" onClick={onCancel}>
                {t("common.action.cancel")}
              </Button>
              <Button data-testid="upload-valid-addresses-btn" onClick={onSubmitValidRows}>
                {t("page.portfolio-address-bulk-upload.btn.import-addresses")}
              </Button>
            </Inline>
          </SectionCard>
        </PageGrid.Column>
      )}
      {amountValidRows > 0 && amountInvalidRows > 0 && (
        <>
          <PageGrid.Column size="100%">
            <h2 className="text-heading-2">{t("page.portfolio-address-bulk-upload.need-your-attention")}</h2>
          </PageGrid.Column>

          <PageGrid.Column size="50%">
            <SectionCard
              title={
                amountInvalidRows >= maxAmountCorrections
                  ? t("page.portfolio-address-bulk-upload.section.max-invalid-addresses-found.title", {
                      count: maxAmountCorrections,
                    })
                  : t("page.portfolio-address-bulk-upload.section.invalid-addresses-found.title", {
                      count: amountInvalidRows,
                    })
              }
              description={
                amountInvalidRows >= maxAmountCorrections
                  ? t("page.portfolio-address-bulk-upload.section.max-invalid-addresses-found.description", {
                      count: maxAmountCorrections,
                    })
                  : t("page.portfolio-address-bulk-upload.section.invalid-addresses-found.description")
              }
              dataTestId={
                amountInvalidRows >= maxAmountCorrections
                  ? "max-invalid-addresses-found-section"
                  : "invalid-addresses-found-section"
              }
              icon={<Tool01Icon />}
            />
          </PageGrid.Column>

          {!!projectColumnInvalidSummary.rowCount && !!projectColumnInvalidSummary.values.length && (
            <PageGrid.Column size="50%">
              <SectionCard
                dataTestId="inaccessible-projects-found-section"
                title={t("page.portfolio-address-bulk-upload.section.addresses-of-inaccessible-projects-found.title", {
                  count: projectColumnInvalidSummary.rowCount,
                })}
                description={t(
                  "page.portfolio-address-bulk-upload.section.addresses-of-inaccessible-projects-found.description",
                )}
                icon={<Building04Icon />}
              >
                <footer className="mt-auto">
                  <PillList
                    label={t("common.entity.project", {
                      count: permittedProjects.length,
                    })}
                    items={projectColumnInvalidSummary.values.map((projectName) => ({
                      label: projectName,
                      icon: <Building04Icon />,
                    }))}
                    maxShown={5}
                  />
                </footer>
              </SectionCard>
            </PageGrid.Column>
          )}
        </>
      )}
    </>
  );
}
